/**
 * The time spine: scales, "nice" ticks and instrument-style timestamps. All times are epoch ms.
 */

export type Zone = 'local' | 'utc'

const S = 1000
const M = 60 * S
const H = 60 * M
const D = 24 * H

/** Candidate tick steps, smallest first. */
export const TICK_STEPS = [
  1 * S, 2 * S, 5 * S, 10 * S, 15 * S, 30 * S,
  1 * M, 2 * M, 5 * M, 10 * M, 15 * M, 30 * M,
  1 * H, 2 * H, 3 * H, 6 * H, 12 * H,
  1 * D, 2 * D, 7 * D, 14 * D, 30 * D, 91 * D, 365 * D,
] as const

export interface Tick {
  t: number
  label: string
  /** Day boundary or otherwise more significant than its neighbours. */
  major: boolean
}

export function linearScale(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain
  const [r0, r1] = range
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0)
  const fn = (t: number) => r0 + (t - d0) * k
  fn.invert = (x: number) => (k === 0 ? d0 : d0 + (x - r0) / k)
  return fn as ((t: number) => number) & { invert: (x: number) => number }
}

/** Smallest step from TICK_STEPS that yields at most `maxTicks` ticks over [start, end]. */
export function tickStep(start: number, end: number, maxTicks: number): number {
  const span = Math.max(1, end - start)
  for (const step of TICK_STEPS) {
    if (span / step <= maxTicks) return step
  }
  return TICK_STEPS[TICK_STEPS.length - 1]!
}

function startOf(t: number, step: number, zone: Zone): number {
  // Align to the step in the display zone so labels land on round clock values.
  const offset = zone === 'local' ? new Date(t).getTimezoneOffset() * M : 0
  if (step >= D) {
    const shifted = t - offset
    return Math.ceil(shifted / D) * D + offset
  }
  const shifted = t - offset
  return Math.ceil(shifted / step) * step + offset
}

export function niceTicks(start: number, end: number, maxTicks = 8, zone: Zone = 'utc'): Tick[] {
  if (!(end > start)) return []
  const step = tickStep(start, end, maxTicks)
  const ticks: Tick[] = []
  let t = startOf(start, step, zone)
  let guard = 0
  while (t <= end && guard++ < 500) {
    const d = parts(t, zone)
    const major = step < D ? d.hour === 0 && d.minute === 0 && d.second === 0 : d.day === 1
    ticks.push({ t, label: tickLabel(t, step, zone), major })
    t += step
  }
  return ticks
}

function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0')
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function parts(t: number, zone: Zone) {
  const d = new Date(t)
  return zone === 'utc'
    ? { year: d.getUTCFullYear(), month: d.getUTCMonth(), day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes(), second: d.getUTCSeconds(), ms: d.getUTCMilliseconds() }
    : { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds(), ms: d.getMilliseconds() }
}

export function tickLabel(t: number, step: number, zone: Zone): string {
  const p = parts(t, zone)
  if (step >= D) return `${pad(p.day)} ${MONTHS[p.month]}`
  if (step >= M) return p.hour === 0 && p.minute === 0 ? `${pad(p.day)} ${MONTHS[p.month]}` : `${pad(p.hour)}:${pad(p.minute)}`
  return `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
}

/** `2026-10-28 14:05:12` (+ ` UTC` when zone is utc). */
export function formatTimestamp(t: number | string | Date, zone: Zone = 'local', withSeconds = true): string {
  const ms = typeof t === 'number' ? t : new Date(t).getTime()
  if (!Number.isFinite(ms)) return '—'
  const p = parts(ms, zone)
  const time = withSeconds ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : `${pad(p.hour)}:${pad(p.minute)}`
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)} ${time}${zone === 'utc' ? ' UTC' : ''}`
}

/** `28 Oct 2026` */
export function formatDate(t: number | string | Date, zone: Zone = 'local'): string {
  const ms = typeof t === 'number' ? t : new Date(t).getTime()
  if (!Number.isFinite(ms)) return '—'
  const p = parts(ms, zone)
  return `${p.day} ${MONTHS[p.month]} ${p.year}`
}

/** `now`, `42s`, `5m`, `3h`, `2d`, then a date. Compact, for dense columns. */
export function formatRelative(t: number | string | Date, now = Date.now()): string {
  const ms = typeof t === 'number' ? t : new Date(t).getTime()
  if (!Number.isFinite(ms)) return '—'
  const diff = now - ms
  const future = diff < 0
  const a = Math.abs(diff)
  let s: string
  if (a < 10 * S) return 'now'
  else if (a < M) s = `${Math.floor(a / S)}s`
  else if (a < H) s = `${Math.floor(a / M)}m`
  else if (a < D) s = `${Math.floor(a / H)}h`
  else if (a < 30 * D) s = `${Math.floor(a / D)}d`
  else return formatDate(ms)
  return future ? `in ${s}` : `${s} ago`
}

/** `+01:00` style offset of the local zone, for column headers that must say which clock they use. */
export function localOffsetLabel(at = Date.now()): string {
  const off = -new Date(at).getTimezoneOffset()
  if (off === 0) return 'UTC'
  const sign = off > 0 ? '+' : '−'
  const a = Math.abs(off)
  return `UTC${sign}${Math.floor(a / 60)}${a % 60 ? `:${pad(a % 60)}` : ''}`
}

/** Buckets event times into `n` equal bins over [start, end] (activity density under a timeline). */
export function bucket(times: readonly number[], start: number, end: number, n: number): number[] {
  const out = new Array<number>(n).fill(0)
  if (!(end > start) || n <= 0) return out
  const w = (end - start) / n
  for (const t of times) {
    if (t < start || t > end) continue
    out[Math.min(n - 1, Math.floor((t - start) / w))]!++
  }
  return out
}

/** Index of the item whose time is closest to `t` in a list sorted by time (either direction). */
export function nearestIndex(times: readonly number[], t: number): number {
  if (times.length === 0) return -1
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < times.length; i++) {
    const d = Math.abs(times[i]! - t)
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

export const DURATION = { S, M, H, D } as const
